import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

import { RequestService } from "services/RequestService";

function AddForm({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: "",
    stId: "",
  });

  const [steamTurbines, setSteamTurbines] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "تنبيه",
    content: "",
    open: false,
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  useEffect(() => {
    fetchSteamTurbines();
  }, []);

  const fetchSteamTurbines = async () => {
    try {
      const response = await RequestService.get("ST");
      if (response.data && !response.data.error) {
        setSteamTurbines(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل التوربينات البخارية",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching steam turbines:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل التوربينات البخارية",
        open: true
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "اسم التوربين مطلوب";
    }
    if (!formData.stId) {
      newErrors.stId = "التوربين البخاري مطلوب";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await RequestService.post("GT", formData);
      if (response.data && !response.data.error) {
        onSuccess(response.data?.message);
        setFormData({
          name: "",
          stId: "",
        });
      } else {
        setErrors({
          submit: response.data?.message || "فشل في إضافة التوربين الغازي"
        });
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في إضافة التوربين الغازي",
          open: true
        });
      }
    } catch (error) {
      console.error("Error adding gas turbine:", error);
      setErrors({
        submit: error.response?.data?.message || "حدث خطأ أثناء إضافة التوربين الغازي"
      });
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء إضافة التوربين الغازي",
        open: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      stId: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          <MDTypography variant="h6">إضافة توربين غازي جديد</MDTypography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <MDBox pt={2} pb={3}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="اسم التوربين"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '45px',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.stId}>
                    <InputLabel>التوربين البخاري</InputLabel>
                    <Select
                      value={formData.stId}
                      name="stId"
                      label="التوربين البخاري"
                      onChange={handleChange}
                      sx={{ 
                        height: "45px",
                        "& .MuiSelect-select": {
                          paddingTop: "10px",
                          paddingBottom: "10px"
                        }
                      }}
                    >
                      {steamTurbines.map((st) => (
                        <MenuItem key={st.id} value={st.id}>
                          {st.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.stId && (
                      <MDTypography variant="caption" color="error">
                        {errors.stId}
                      </MDTypography>
                    )}
                  </FormControl>
                </Grid>
                {errors.submit && (
                  <Grid item xs={12}>
                    <MDTypography color="error" variant="caption">
                      {errors.submit}
                    </MDTypography>
                  </Grid>
                )}
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions sx={{ 
            padding: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            '& > button': {
              marginLeft: '8px',
              marginRight: '0px',
            }
          }}>
            <MDButton type="submit" color="info" disabled={isSubmitting}>
              {isSubmitting ? "جاري الإضافة..." : "إضافة"}
            </MDButton>
            <MDButton onClick={handleClose} color="secondary">
              إلغاء
            </MDButton>
          </DialogActions>
        </form>
      </Dialog>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </>
  );
}

AddForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddForm;
